// Offline Storage Utility for Employee Access System
// Handles storing failed requests and retrying them when connection is restored

class OfflineStorage {
  constructor() {
    this.dbName = 'EmployeeAccessOfflineDB';
    this.dbVersion = 1;
    this.db = null;
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second base delay
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Store for failed requests
        if (!db.objectStoreNames.contains('failedRequests')) {
          const store = db.createObjectStore('failedRequests', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('endpoint', 'endpoint', { unique: false });
          store.createIndex('retryCount', 'retryCount', { unique: false });
        }

        // Store for pending actions
        if (!db.objectStoreNames.contains('pendingActions')) {
          const store = db.createObjectStore('pendingActions', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('action', 'action', { unique: false });
        }
      };
    });
  }

  async storeFailedRequest(requestData) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['failedRequests'], 'readwrite');
    const store = transaction.objectStore('failedRequests');
    
    const failedRequest = {
      ...requestData,
      timestamp: Date.now(),
      retryCount: 0,
      lastRetry: null
    };

    return new Promise((resolve, reject) => {
      const request = store.add(failedRequest);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async storePendingAction(actionData) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['pendingActions'], 'readwrite');
    const store = transaction.objectStore('pendingActions');
    
    const pendingAction = {
      ...actionData,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      const request = store.add(pendingAction);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getFailedRequests() {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['failedRequests'], 'readonly');
    const store = transaction.objectStore('failedRequests');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingActions() {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['pendingActions'], 'readonly');
    const store = transaction.objectStore('pendingActions');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removeFailedRequest(id) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['failedRequests'], 'readwrite');
    const store = transaction.objectStore('failedRequests');
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async removePendingAction(id) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['pendingActions'], 'readwrite');
    const store = transaction.objectStore('pendingActions');
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async updateRetryCount(id, retryCount) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['failedRequests'], 'readwrite');
    const store = transaction.objectStore('failedRequests');
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        const data = getRequest.result;
        if (data) {
          data.retryCount = retryCount;
          data.lastRetry = Date.now();
          const putRequest = store.put(data);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Request not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async clearAll() {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['failedRequests', 'pendingActions'], 'readwrite');
    
    return Promise.all([
      new Promise((resolve, reject) => {
        const request = transaction.objectStore('failedRequests').clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise((resolve, reject) => {
        const request = transaction.objectStore('pendingActions').clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ]);
  }
}

export default new OfflineStorage();
